<template>
  <div class="app-shell">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark">
          <ShieldCheck :size="24" stroke-width="2.2" />
        </div>
        <div>
          <p class="eyebrow">Digital Delivery</p>
          <h1>卡密交付中心</h1>
        </div>
      </div>

      <a v-if="view === 'admin'" class="top-link" href="/">
        <FileJson :size="18" />
        返回首页
      </a>
    </header>

    <main>
      <section v-if="view === 'redeem'" class="workspace redeem-workspace">
        <div class="surface redeem-card">
          <div class="section-heading">
            <div>
              <h2>输入卡密领取文件</h2>
            </div>
            <FileJson :size="28" />
          </div>

          <form class="redeem-form" @submit.prevent="redeem">
            <label for="redeemCode">卡密</label>
            <div class="input-row">
              <input
                id="redeemCode"
                v-model.trim="redeemCode"
                autocomplete="one-time-code"
                placeholder="KM-ABCD-1234-5678"
              >
              <button class="primary-button" type="submit" :disabled="redeemLoading">
                <Loader2 v-if="redeemLoading" class="spin" :size="18" />
                <Search v-else :size="18" />
                查询
              </button>
            </div>
          </form>

          <p v-if="redeemError" class="feedback error">
            <XCircle :size="18" />
            {{ redeemError }}
          </p>

          <div v-if="claim" class="claim-panel">
            <div class="claim-title">
              <CheckCircle2 :size="22" />
              <div>
                <span>卡密有效</span>
                <strong>交付资料</strong>
              </div>
            </div>
            <dl>
              <div>
                <dt>交付信息</dt>
                <dd>
                  <pre class="delivery-info">{{ claim.item.deliveryInfo || claim.item.deliveryNote || '无交付信息' }}</pre>
                </dd>
              </div>
              <div>
                <dt>文件</dt>
                <dd>{{ claim.item.fileName }}</dd>
              </div>
            </dl>
            <div class="claim-actions">
              <button class="ghost-button full" type="button" @click="copyDeliveryInfo(claim.item.deliveryInfo || claim.item.deliveryNote || '')">
                <Clipboard :size="18" />
                复制交付信息
              </button>
              <a class="download-button" :href="downloadUrl(claim.claimToken)">
                <Download :size="18" />
                下载 JSON
              </a>
            </div>
          </div>
        </div>

        <aside class="surface status-card">
          <div class="section-heading compact">
            <div>
              <p class="eyebrow">Status</p>
              <h2>交付状态</h2>
            </div>
            <Activity :size="26" />
          </div>
          <div class="status-list">
            <div>
              <span>卡密验证</span>
              <strong>实时</strong>
            </div>
            <div>
              <span>下载窗口</span>
              <strong>30 分钟</strong>
            </div>
            <div>
              <span>文件类型</span>
              <strong>JSON</strong>
            </div>
          </div>
          <div class="support-panel">
            <div class="support-title">
              <MessageCircle :size="20" />
              <span>微信售后</span>
            </div>
            <img class="wechat-qr" src="/wechat.jpg" alt="微信售后二维码">
          </div>
          <p class="policy-note">
            本系统用于合法数字资料交付，交付内容由管理员自行确认并维护。
          </p>
        </aside>
      </section>

      <section v-else class="workspace admin-workspace">
        <div v-if="!adminToken" class="surface login-card">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Admin</p>
              <h2>管理登录</h2>
            </div>
            <Lock :size="28" />
          </div>

          <form class="stack-form" @submit.prevent="login">
            <label for="adminPassword">管理密码</label>
            <input
              id="adminPassword"
              v-model="adminPassword"
              type="password"
              autocomplete="current-password"
              placeholder="请输入管理密码"
            >
            <button class="primary-button full" type="submit" :disabled="adminLoading">
              <Loader2 v-if="adminLoading" class="spin" :size="18" />
              <ShieldCheck v-else :size="18" />
              登录
            </button>
          </form>

          <p v-if="adminError" class="feedback error">
            <XCircle :size="18" />
            {{ adminError }}
          </p>
        </div>

        <template v-else>
          <div class="admin-toolbar">
            <div>
              <p class="eyebrow">Console</p>
              <h2>交付管理</h2>
            </div>
            <button class="ghost-button" type="button" @click="logout">
              <LogOut :size="18" />
              退出
            </button>
          </div>

          <div class="metric-grid">
            <div class="metric">
              <Database :size="20" />
              <span>总卡密</span>
              <strong>{{ adminStats.total }}</strong>
            </div>
            <div class="metric">
              <CheckCircle2 :size="20" />
              <span>启用中</span>
              <strong>{{ adminStats.active }}</strong>
            </div>
            <div class="metric">
              <ClipboardCheck :size="20" />
              <span>已领取</span>
              <strong>{{ adminStats.claimed }}</strong>
            </div>
            <div class="metric">
              <Download :size="20" />
              <span>下载次数</span>
              <strong>{{ adminStats.downloads }}</strong>
            </div>
          </div>

          <div class="admin-grid">
            <form class="surface create-card" @submit.prevent="createItem">
              <div class="section-heading compact">
                <div>
                  <p class="eyebrow">Create</p>
                  <h2>新建交付</h2>
                </div>
                <Plus :size="26" />
              </div>

              <label for="deliveryInfo">交付信息</label>
              <textarea
                id="deliveryInfo"
                v-model.trim="uploadForm.deliveryInfo"
                maxlength="50000"
                rows="12"
                placeholder="粘贴需要随卡密交付的文本信息"
              />
              <div class="field-count">{{ uploadForm.deliveryInfo.length }} / 50000</div>

              <label class="file-box" for="jsonFile">
                <UploadCloud :size="24" />
                <span>{{ uploadForm.file ? uploadForm.file.name : '选择 JSON 文件' }}</span>
                <input
                  id="jsonFile"
                  ref="jsonFile"
                  type="file"
                  accept="application/json,.json"
                  @change="onFileChange"
                >
              </label>

              <button class="primary-button full" type="submit" :disabled="createLoading">
                <Loader2 v-if="createLoading" class="spin" :size="18" />
                <Plus v-else :size="18" />
                生成卡密
              </button>

              <p v-if="createError" class="feedback error">
                <XCircle :size="18" />
                {{ createError }}
              </p>
              <p v-if="createSuccess" class="feedback success">
                <CheckCircle2 :size="18" />
                {{ createSuccess }}
              </p>
            </form>

            <section class="surface list-card">
              <div class="section-heading compact">
                <div>
                  <p class="eyebrow">Records</p>
                  <h2>卡密列表</h2>
                </div>
                <button class="icon-button" type="button" title="刷新" @click="fetchItems">
                  <Activity :size="18" />
                </button>
              </div>

              <p v-if="toast" class="feedback success">
                <Clipboard :size="18" />
                {{ toast }}
              </p>

              <div v-if="itemsLoading" class="empty-state">
                <Loader2 class="spin" :size="24" />
                <span>正在加载</span>
              </div>
              <div v-else-if="items.length === 0" class="empty-state">
                <FileJson :size="26" />
                <span>暂无卡密</span>
              </div>
              <div v-else class="item-list">
                <article v-for="item in items" :key="item.id" class="item-row">
                  <div class="item-main">
                    <div class="item-title">
                      <strong>{{ item.fileName }}</strong>
                      <div class="item-badges">
                        <span :class="['status-pill', hasRedeemed(item) ? 'claimed' : 'pending']">
                          {{ hasRedeemed(item) ? '已领取' : '未领取' }}
                        </span>
                        <span :class="['status-pill', item.active ? 'on' : 'off']">
                          {{ item.active ? '启用' : '停用' }}
                        </span>
                      </div>
                    </div>
                    <button class="code-chip" type="button" @click="copyCode(item.code)">
                      <Clipboard :size="15" />
                      {{ item.code }}
                    </button>
                    <div class="item-meta">
                      <span>{{ previewInfo(item) }}</span>
                      <span>{{ redeemText(item) }}</span>
                      <span>{{ item.downloadCount }} 次下载</span>
                      <span>{{ formatDate(item.createdAt) }}</span>
                    </div>
                  </div>
                  <div class="item-actions">
                    <button
                      class="ghost-button small"
                      type="button"
                      @click="openEdit(item)"
                    >
                      <Pencil :size="16" />
                      编辑
                    </button>
                    <button
                      class="ghost-button small"
                      type="button"
                      @click="toggleItem(item)"
                    >
                      <CircleAlert :size="16" />
                      {{ item.active ? '停用' : '启用' }}
                    </button>
                    <button
                      class="danger-button small"
                      type="button"
                      @click="deleteItem(item)"
                    >
                      <Trash2 :size="16" />
                      删除
                    </button>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </template>
      </section>
    </main>

    <div v-if="editingItem" class="modal-layer" role="dialog" aria-modal="true" aria-labelledby="editTitle">
      <form class="modal-panel" @submit.prevent="updateItem">
        <div class="section-heading compact">
          <div>
            <p class="eyebrow">Edit</p>
            <h2 id="editTitle">编辑卡密</h2>
          </div>
          <button class="icon-button" type="button" title="关闭" @click="closeEdit">
            <X :size="18" />
          </button>
        </div>

        <label for="editDeliveryInfo">交付信息</label>
        <textarea
          id="editDeliveryInfo"
          v-model.trim="editForm.deliveryInfo"
          maxlength="50000"
          rows="10"
          placeholder="修改随卡密交付的文本信息"
        />
        <div class="field-count">{{ editForm.deliveryInfo.length }} / 50000</div>

        <label class="file-box" for="editJsonFile">
          <UploadCloud :size="24" />
          <span>{{ editForm.file ? editForm.file.name : `保留当前文件：${editForm.fileName}` }}</span>
          <input
            id="editJsonFile"
            ref="editJsonFile"
            type="file"
            accept="application/json,.json"
            @change="onEditFileChange"
          >
        </label>

        <div class="modal-actions">
          <button class="ghost-button full" type="button" @click="closeEdit">
            <X :size="18" />
            取消
          </button>
          <button class="primary-button full" type="submit" :disabled="editLoading">
            <Loader2 v-if="editLoading" class="spin" :size="18" />
            <Save v-else :size="18" />
            保存
          </button>
        </div>

        <p v-if="editError" class="feedback error">
          <XCircle :size="18" />
          {{ editError }}
        </p>
      </form>
    </div>
  </div>
</template>

<script>
import {
  Activity,
  CheckCircle2,
  CircleAlert,
  Clipboard,
  ClipboardCheck,
  Database,
  Download,
  FileJson,
  Loader2,
  Lock,
  LogOut,
  MessageCircle,
  Pencil,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
  XCircle
} from 'lucide-vue-next'

const tokenKey = 'kami-admin-token'
const adminPath = '/admin-kami-q8z4p7v2'

export default {
  name: 'App',
  components: {
    Activity,
    CheckCircle2,
    CircleAlert,
    Clipboard,
    ClipboardCheck,
    Database,
    Download,
    FileJson,
    Loader2,
    Lock,
    LogOut,
    MessageCircle,
    Pencil,
    Plus,
    Save,
    Search,
    ShieldCheck,
    Trash2,
    UploadCloud,
    X,
    XCircle
  },
  data() {
    return {
      view: 'redeem',
      redeemCode: '',
      redeemLoading: false,
      redeemError: '',
      claim: null,
      adminPassword: '',
      adminToken: '',
      adminLoading: false,
      adminError: '',
      items: [],
      itemsLoading: false,
      createLoading: false,
      createError: '',
      createSuccess: '',
      editingItem: null,
      editLoading: false,
      editError: '',
      toast: '',
      uploadForm: {
        deliveryInfo: '',
        file: null
      },
      editForm: {
        deliveryInfo: '',
        fileName: '',
        file: null
      }
    }
  },
  computed: {
    adminStats() {
      return {
        total: this.items.length,
        active: this.items.filter((item) => item.active).length,
        claimed: this.items.filter((item) => this.hasRedeemed(item)).length,
        downloads: this.items.reduce((total, item) => total + Number(item.downloadCount || 0), 0)
      }
    }
  },
  mounted() {
    this.adminToken = localStorage.getItem(tokenKey) || ''
    this.syncViewFromLocation()
    window.addEventListener('popstate', this.syncViewFromLocation)
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.syncViewFromLocation)
  },
  methods: {
    syncViewFromLocation() {
      this.view = window.location.pathname.replace(/\/$/, '') === adminPath ? 'admin' : 'redeem'
      if (this.view === 'admin' && this.adminToken) {
        this.fetchItems()
      }
    },
    async api(path, options = {}, admin = false) {
      const headers = { ...(options.headers || {}) }
      const isFormData = options.body instanceof FormData
      if (options.body && !isFormData && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json'
      }
      if (admin) {
        headers.Authorization = `Bearer ${this.adminToken}`
      }

      const response = await fetch(path, {
        ...options,
        headers
      })
      const contentType = response.headers.get('content-type') || ''
      const payload = contentType.includes('application/json')
        ? await response.json()
        : await response.text()

      if (!response.ok) {
        throw new Error(payload.error || payload || '请求失败')
      }
      return payload
    },
    async redeem() {
      this.redeemError = ''
      this.claim = null
      if (!this.redeemCode) {
        this.redeemError = '请输入卡密'
        return
      }

      this.redeemLoading = true
      try {
        this.claim = await this.api('/api/redeem', {
          method: 'POST',
          body: JSON.stringify({ code: this.redeemCode })
        })
      } catch (error) {
        this.redeemError = error.message
      } finally {
        this.redeemLoading = false
      }
    },
    async login() {
      this.adminError = ''
      if (!this.adminPassword) {
        this.adminError = '请输入管理密码'
        return
      }

      this.adminLoading = true
      try {
        const data = await this.api('/api/admin/login', {
          method: 'POST',
          body: JSON.stringify({ password: this.adminPassword })
        })
        this.adminToken = data.token
        localStorage.setItem(tokenKey, data.token)
        this.adminPassword = ''
        await this.fetchItems()
      } catch (error) {
        this.adminError = error.message
      } finally {
        this.adminLoading = false
      }
    },
    logout() {
      this.adminToken = ''
      this.items = []
      localStorage.removeItem(tokenKey)
    },
    async fetchItems() {
      if (!this.adminToken) {
        return
      }

      this.itemsLoading = true
      try {
        const data = await this.api('/api/admin/items', {}, true)
        this.items = data.items
      } catch (error) {
        this.adminError = error.message
        if (error.message.includes('登录')) {
          this.logout()
        }
      } finally {
        this.itemsLoading = false
      }
    },
    onFileChange(event) {
      const [file] = event.target.files
      this.uploadForm.file = file || null
    },
    async createItem() {
      this.createError = ''
      this.createSuccess = ''

      if (!this.uploadForm.deliveryInfo || !this.uploadForm.file) {
        this.createError = '请填写交付信息并选择 JSON 文件'
        return
      }

      const formData = new FormData()
      formData.append('deliveryInfo', this.uploadForm.deliveryInfo)
      formData.append('jsonFile', this.uploadForm.file)

      this.createLoading = true
      try {
        const data = await this.api('/api/admin/items', {
          method: 'POST',
          body: formData
        }, true)
        this.items.unshift(data.item)
        this.createSuccess = `已生成 ${data.item.code}`
        this.uploadForm = {
          deliveryInfo: '',
          file: null
        }
        if (this.$refs.jsonFile) {
          this.$refs.jsonFile.value = ''
        }
      } catch (error) {
        this.createError = error.message
      } finally {
        this.createLoading = false
      }
    },
    async toggleItem(item) {
      try {
        const data = await this.api(`/api/admin/items/${item.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ active: !item.active })
        }, true)
        this.items = this.items.map((entry) => (entry.id === item.id ? data.item : entry))
      } catch (error) {
        this.toast = error.message
      }
    },
    openEdit(item) {
      this.editingItem = item
      this.editError = ''
      this.editForm = {
        deliveryInfo: item.deliveryInfo || item.deliveryNote || '',
        fileName: item.fileName,
        file: null
      }
      if (this.$refs.editJsonFile) {
        this.$refs.editJsonFile.value = ''
      }
    },
    closeEdit() {
      this.editingItem = null
      this.editError = ''
      this.editLoading = false
      this.editForm = {
        deliveryInfo: '',
        fileName: '',
        file: null
      }
    },
    onEditFileChange(event) {
      const [file] = event.target.files
      this.editForm.file = file || null
    },
    async updateItem() {
      this.editError = ''
      if (!this.editingItem) {
        return
      }
      if (!this.editForm.deliveryInfo) {
        this.editError = '请填写交付信息'
        return
      }

      const formData = new FormData()
      formData.append('deliveryInfo', this.editForm.deliveryInfo)
      if (this.editForm.file) {
        formData.append('jsonFile', this.editForm.file)
      }

      this.editLoading = true
      try {
        const data = await this.api(`/api/admin/items/${this.editingItem.id}`, {
          method: 'PATCH',
          body: formData
        }, true)
        this.items = this.items.map((entry) => (entry.id === data.item.id ? data.item : entry))
        this.toast = `已更新 ${data.item.code}`
        this.closeEdit()
        window.setTimeout(() => {
          this.toast = ''
        }, 2200)
      } catch (error) {
        this.editError = error.message
      } finally {
        this.editLoading = false
      }
    },
    async deleteItem(item) {
      if (!window.confirm(`删除卡密 ${item.code}？`)) {
        return
      }

      try {
        await this.api(`/api/admin/items/${item.id}`, {
          method: 'DELETE'
        }, true)
        this.items = this.items.filter((entry) => entry.id !== item.id)
      } catch (error) {
        this.toast = error.message
      }
    },
    async copyCode(code) {
      await navigator.clipboard.writeText(code)
      this.toast = `已复制 ${code}`
      window.setTimeout(() => {
        this.toast = ''
      }, 2200)
    },
    async copyDeliveryInfo(text) {
      if (!text) {
        return
      }
      await navigator.clipboard.writeText(text)
      this.toast = '已复制交付信息'
      window.setTimeout(() => {
        this.toast = ''
      }, 2200)
    },
    previewInfo(item) {
      const text = String(item.deliveryInfo || item.deliveryNote || '').trim()
      if (!text) {
        return '无交付信息'
      }
      return `交付信息 ${text.length} 字符`
    },
    hasRedeemed(item) {
      return Number(item.redeemCount || 0) > 0 || Boolean(item.lastRedeemedAt)
    },
    redeemText(item) {
      if (!this.hasRedeemed(item)) {
        return '未领取'
      }
      return `${Number(item.redeemCount || 1)} 次领取`
    },
    downloadUrl(token) {
      return `/api/download/${encodeURIComponent(token)}`
    },
    formatDate(value) {
      if (!value) {
        return '--'
      }
      return new Intl.DateTimeFormat('zh-CN', {
        dateStyle: 'short',
        timeStyle: 'short',
        hour12: false
      }).format(new Date(value))
    }
  }
}
</script>

<style>
:root {
  color-scheme: light;
  --bg: #f5f7f3;
  --surface: #ffffff;
  --surface-soft: #eef4f1;
  --text: #18211f;
  --muted: #68736f;
  --line: #dfe6e2;
  --primary: #176b5c;
  --primary-dark: #0f4f44;
  --accent: #c87920;
  --danger: #b42318;
  --danger-soft: #fff0ed;
  --success: #23764f;
  --success-soft: #eaf7ef;
  --shadow: 0 18px 45px rgba(18, 33, 31, 0.09);
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  background:
    linear-gradient(135deg, rgba(23, 107, 92, 0.09), transparent 34%),
    linear-gradient(225deg, rgba(200, 121, 32, 0.09), transparent 30%),
    var(--bg);
  color: var(--text);
  font-family: Inter, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

button,
input,
textarea {
  font: inherit;
}

button,
a {
  -webkit-tap-highlight-color: transparent;
}

.app-shell {
  min-height: 100vh;
  padding: 18px;
}

.topbar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1180px;
  padding: 14px 0 22px;
}

.brand,
.section-heading,
.admin-toolbar,
.claim-title,
.metric,
.feedback,
.item-title,
.item-actions,
.code-chip,
.download-button,
.primary-button,
.ghost-button,
.danger-button,
.top-link {
  align-items: center;
  display: flex;
}

.brand {
  gap: 12px;
  min-width: 0;
}

.brand-mark {
  align-items: center;
  background: var(--primary);
  border-radius: 8px;
  color: white;
  display: grid;
  flex: 0 0 46px;
  height: 46px;
  place-items: center;
  width: 46px;
}

.eyebrow {
  color: var(--accent);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 4px;
  text-transform: uppercase;
}

h1,
h2 {
  letter-spacing: 0;
  margin: 0;
}

h1 {
  font-size: clamp(24px, 4vw, 34px);
  line-height: 1.1;
}

h2 {
  font-size: 22px;
  line-height: 1.2;
}

.top-link {
  background: var(--primary);
  border-radius: 8px;
  box-shadow: 0 8px 18px rgba(23, 107, 92, 0.18);
  color: white;
  font-weight: 800;
  gap: 7px;
  min-height: 42px;
  padding: 0 14px;
  text-decoration: none;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.top-link:hover {
  background: var(--primary-dark);
  box-shadow: 0 10px 24px rgba(23, 107, 92, 0.22);
  transform: translateY(-1px);
}

main {
  margin: 0 auto;
  max-width: 1180px;
}

.workspace {
  display: grid;
  gap: 18px;
}

.redeem-workspace {
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
}

.surface {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(223, 230, 226, 0.88);
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 22px;
}

.redeem-card,
.login-card {
  min-height: 430px;
}

.login-card {
  margin: 18px auto 0;
  max-width: 460px;
  width: 100%;
}

.section-heading {
  color: var(--primary);
  justify-content: space-between;
  margin-bottom: 24px;
}

.section-heading.compact {
  margin-bottom: 18px;
}

label {
  color: var(--text);
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

input,
textarea {
  background: #fbfcfb;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--text);
  outline: none;
  padding: 13px 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  width: 100%;
}

textarea {
  min-height: 112px;
  resize: vertical;
}

input:focus,
textarea:focus {
  background: white;
  border-color: rgba(23, 107, 92, 0.6);
  box-shadow: 0 0 0 4px rgba(23, 107, 92, 0.12);
}

.field-count {
  color: var(--muted);
  font-size: 12px;
  margin: -6px 0 4px;
  text-align: right;
}

.input-row {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
}

.primary-button,
.download-button,
.ghost-button,
.danger-button,
.icon-button {
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 800;
  justify-content: center;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  white-space: nowrap;
}

.primary-button,
.download-button {
  background: var(--primary);
  color: white;
  gap: 8px;
  min-height: 48px;
  padding: 0 18px;
}

.primary-button:hover,
.download-button:hover {
  background: var(--primary-dark);
  box-shadow: 0 10px 24px rgba(23, 107, 92, 0.22);
  transform: translateY(-1px);
}

.primary-button:disabled {
  cursor: not-allowed;
  opacity: 0.68;
  transform: none;
}

.primary-button.full,
.download-button,
.ghost-button.full {
  width: 100%;
}

.ghost-button,
.danger-button {
  background: #f4f7f5;
  color: var(--text);
  gap: 7px;
  min-height: 40px;
  padding: 0 12px;
}

.ghost-button:hover {
  background: var(--surface-soft);
}

.danger-button {
  background: var(--danger-soft);
  color: var(--danger);
}

.danger-button:hover {
  background: #ffe2dc;
}

.small {
  min-height: 36px;
}

.icon-button {
  align-items: center;
  background: var(--surface-soft);
  color: var(--primary);
  display: grid;
  height: 38px;
  place-items: center;
  width: 38px;
}

.feedback {
  border-radius: 8px;
  gap: 8px;
  margin: 14px 0 0;
  padding: 12px;
}

.feedback.error {
  background: var(--danger-soft);
  color: var(--danger);
}

.feedback.success {
  background: var(--success-soft);
  color: var(--success);
}

.claim-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  margin-top: 22px;
  padding: 18px;
}

.claim-title {
  color: var(--success);
  gap: 10px;
  margin-bottom: 16px;
}

.claim-title span {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

.claim-title strong {
  color: var(--text);
  display: block;
  font-size: 20px;
  margin-top: 2px;
}

dl {
  display: grid;
  gap: 12px;
  margin: 0 0 18px;
}

dl div {
  border-bottom: 1px solid var(--line);
  display: grid;
  gap: 4px;
  padding-bottom: 12px;
}

dt {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.delivery-info {
  background: #fbfcfb;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--text);
  line-height: 1.65;
  margin: 0;
  max-height: 260px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.claim-actions {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
}

.status-list {
  display: grid;
  gap: 10px;
}

.status-list div {
  align-items: center;
  background: #fbfcfb;
  border: 1px solid var(--line);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  padding: 14px;
}

.support-panel {
  border-top: 1px solid var(--line);
  margin-top: 18px;
  padding-top: 18px;
}

.support-title {
  align-items: center;
  color: var(--primary);
  display: flex;
  font-weight: 800;
  gap: 8px;
  margin-bottom: 12px;
}

.wechat-qr {
  aspect-ratio: 1 / 1;
  background: white;
  border: 1px solid var(--line);
  border-radius: 8px;
  display: block;
  object-fit: cover;
  width: min(100%, 260px);
}

.status-list span,
.item-meta,
.policy-note {
  color: var(--muted);
}

.policy-note {
  border-top: 1px solid var(--line);
  line-height: 1.7;
  margin: 18px 0 0;
  padding-top: 16px;
}

.stack-form,
.create-card {
  display: grid;
  gap: 12px;
}

.admin-toolbar {
  justify-content: space-between;
  margin-bottom: 14px;
}

.admin-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(320px, 0.75fr) minmax(0, 1.25fr);
}

.metric-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 18px;
}

.metric {
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--primary);
  gap: 10px;
  min-height: 78px;
  padding: 16px;
}

.metric span {
  color: var(--muted);
  font-size: 13px;
  margin-right: auto;
}

.metric strong {
  color: var(--text);
  font-size: 24px;
}

.file-box {
  align-items: center;
  background: #fbfcfb;
  border: 1px dashed rgba(23, 107, 92, 0.42);
  border-radius: 8px;
  color: var(--primary);
  cursor: pointer;
  display: flex;
  gap: 10px;
  justify-content: center;
  min-height: 84px;
  padding: 14px;
  text-align: center;
}

.file-box input {
  display: none;
}

.list-card {
  min-width: 0;
}

.empty-state {
  align-items: center;
  color: var(--muted);
  display: grid;
  gap: 10px;
  min-height: 220px;
  place-items: center;
}

.item-list {
  display: grid;
  gap: 12px;
}

.item-row {
  border: 1px solid var(--line);
  border-radius: 8px;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 14px;
}

.item-main {
  min-width: 0;
}

.item-title {
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
}

.item-title strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-badges {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.status-pill {
  border-radius: 999px;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  padding: 4px 8px;
}

.status-pill.on {
  background: var(--success-soft);
  color: var(--success);
}

.status-pill.off {
  background: #eceff2;
  color: #667085;
}

.status-pill.claimed {
  background: #edf7ff;
  color: #175cd3;
}

.status-pill.pending {
  background: #fff7ed;
  color: #b54708;
}

.code-chip {
  background: var(--surface-soft);
  border: 0;
  border-radius: 6px;
  color: var(--primary);
  cursor: pointer;
  gap: 6px;
  margin-top: 10px;
  max-width: 100%;
  min-height: 34px;
  overflow-wrap: anywhere;
  padding: 7px 9px;
  text-align: left;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px 14px;
  margin-top: 10px;
}

.item-actions {
  align-content: start;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.modal-layer {
  align-items: center;
  background: rgba(15, 31, 28, 0.42);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 18px;
  position: fixed;
  z-index: 20;
}

.modal-panel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: 0 24px 70px rgba(15, 31, 28, 0.22);
  display: grid;
  gap: 12px;
  max-height: calc(100vh - 36px);
  max-width: 560px;
  overflow: auto;
  padding: 22px;
  width: min(560px, 100%);
}

.modal-actions {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
}

.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .topbar {
    align-items: stretch;
    flex-direction: column;
  }

  .top-link {
    align-self: flex-start;
  }

  .redeem-workspace,
  .admin-grid {
    grid-template-columns: 1fr;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .app-shell {
    padding: 12px;
  }

  .surface {
    padding: 16px;
  }

  .brand-mark {
    flex-basis: 42px;
    height: 42px;
    width: 42px;
  }

  .input-row,
  .claim-actions,
  .modal-actions,
  .item-row {
    grid-template-columns: 1fr;
  }

  .item-actions {
    justify-content: stretch;
  }

  .item-actions button {
    flex: 1;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 20px;
  }
}
</style>
